import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { logAudit } from '../auditLog';
import { CATEGORIAS_PELIGRO } from '../lib/iperCalculations';
import './JobPositionsPage.css';
import InformesPuestoPanel from '../components/informes-puesto/InformesPuestoPanel';
import LicenciasPuestoCampo from '../components/licencias/LicenciasPuestoCampo';

// ── Types ──────────────────────────────────────────────────────────────────────

interface JobPosition {
  id:          string;
  company_id:  string;
  name:        string;
  description: string | null;
  created_at:  string;
  numero_trabajadores?: number | null;
  actividades_realizadas?: string | null;
  horas_actividad_diarias?: number | null;
  recursos_utilizados?: string | null;
  materiales_agentes?: string | null;
  opera_vehiculos?: boolean | null;          // M61 (TH 5)
  licencias_aceptadas?: string[] | null;
}

interface IperHazardCatalogItem {
  id:            string;
  category:      string;
  hazard_type:   string;
  display_order: number;
}

// Texto del modal propio para eliminar un puesto (PE-A4c; antes se borraba sin confirmar). Con trabajadores activos no
// se puede borrar; los que no están activos quedan sin puesto (FK ON DELETE SET NULL) y las evaluaciones ligadas al
// puesto (iluminación, ruido, ergonomía, estrés térmico, BIOGAVAL) se borran con él (ON DELETE CASCADE).
export function mensajeBorrarPuesto(nombre: string, activos: number, otros: number): string {
  const n = (k: number, uno: string, varios: string) => `${k} ${k === 1 ? uno : varios}`;
  const asignados = activos + otros;
  const quienes = asignados === 0
    ? 'No tiene trabajadores asignados.'
    : `Tiene ${n(asignados, 'trabajador asignado', 'trabajadores asignados')}`
      + (activos > 0 ? ` (${n(activos, 'activo', 'activos')}): no se puede eliminar mientras tenga trabajadores activos.`
        : ` que no ${otros === 1 ? 'está activo' : 'están activos'} (en licencia o desvinculados): ${otros === 1 ? 'queda' : 'quedan'} sin puesto.`);
  return `Se eliminará el puesto "${nombre}". ${quienes} También se borran las evaluaciones hechas para este puesto `
    + '(iluminación, ruido, ergonomía, estrés térmico y BIOGAVAL). Esta acción no se puede deshacer.';
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function JobPositionsPage() {
  const { company, profile } = useCompany();
  const { impersonated }  = useImpersonation();
  const effectiveCompanyId = (impersonated ?? company)?.id ?? '';

  const [positions,    setPositions]    = useState<JobPosition[]>([]);
  const [workerCounts, setWorkerCounts] = useState<Record<string, number>>({});
  const [otherWorkerCounts, setOtherWorkerCounts] = useState<Record<string, number>>({});
  const [porBorrar, setPorBorrar] = useState<JobPosition | null>(null);
  // Lote D: informes consolidados por puesto (GT 5, GT 6 y GT 7).
  const [verInformes, setVerInformes] = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [fetchError,   setFetchError]   = useState('');
  const [deletingId,   setDeletingId]   = useState<string | null>(null);
  const [toast,        setToast]        = useState('');

  // modal state
  const [modalOpen,  setModalOpen]  = useState(false);
  const [editing,    setEditing]    = useState<JobPosition | null>(null);
  const [formName,   setFormName]   = useState('');
  const [formDesc,   setFormDesc]   = useState('');
  const [formNumeroTrabajadores,    setFormNumeroTrabajadores]    = useState('');
  const [formActividadesRealizadas, setFormActividadesRealizadas] = useState('');
  const [formHorasActividad,        setFormHorasActividad]        = useState('');
  const [formRecursosUtilizados,    setFormRecursosUtilizados]    = useState('');
  const [formMaterialesAgentes,     setFormMaterialesAgentes]     = useState('');
  const [formOperaVehiculos,        setFormOperaVehiculos]        = useState(false);   // M61 (TH 5)
  const [formLicencias,             setFormLicencias]             = useState<string[]>([]);
  const [formError,  setFormError]  = useState('');
  const [saving,     setSaving]     = useState(false);

  // riesgos típicos del puesto
  const [catalogItems, setCatalogItems]             = useState<IperHazardCatalogItem[]>([]);
  const [selectedHazards, setSelectedHazards]       = useState<Set<string>>(new Set());
  const [hazardCustomText, setHazardCustomText]     = useState<Map<string, string>>(new Map());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // ── Data loading ───────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    if (!effectiveCompanyId) return;
    setLoading(true);
    setFetchError('');

    const [{ data: posData, error: e1 }, { data: wkData, error: e2 }] = await Promise.all([
      supabase
        .from('job_positions')
        .select('*')
        .eq('company_id', effectiveCompanyId)
        .order('name'),
      supabase
        .from('workers')
        .select('position_id, status')
        .eq('company_id', effectiveCompanyId)
        .not('position_id', 'is', null),
    ]);

    if (e1 || e2) {
      setFetchError('Error al cargar puestos de trabajo. Intenta de nuevo.');
      setLoading(false);
      return;
    }

    setPositions(posData ?? []);

    // Activos: bloquean el borrado (como antes). Los demás (en licencia o desvinculados) solo se avisan en el modal.
    const counts: Record<string, number> = {};
    const otros: Record<string, number> = {};
    (wkData ?? []).forEach((w: { position_id: string; status: string | null }) => {
      const destino = w.status === 'activo' ? counts : otros;
      destino[w.position_id] = (destino[w.position_id] ?? 0) + 1;
    });
    setWorkerCounts(counts);
    setOtherWorkerCounts(otros);
    setLoading(false);
  }, [effectiveCompanyId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Toast ──────────────────────────────────────────────────────────────────

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // ── Modal helpers ──────────────────────────────────────────────────────────

  const openCreate = async () => {
    setEditing(null);
    setFormName('');
    setFormDesc('');
    setFormNumeroTrabajadores('');
    setFormActividadesRealizadas('');
    setFormHorasActividad('');
    setFormRecursosUtilizados('');
    setFormMaterialesAgentes('');
    setFormOperaVehiculos(false);
    setFormLicencias([]);
    setFormError('');
    setSelectedHazards(new Set());
    setHazardCustomText(new Map());
    setModalOpen(true);

    const { data: catalogData } = await supabase
      .from('iper_hazard_catalog')
      .select('*')
      .order('category')
      .order('display_order');
    setCatalogItems((catalogData ?? []) as IperHazardCatalogItem[]);
  };

  const openEdit = async (pos: JobPosition) => {
    setEditing(pos);
    setFormName(pos.name);
    setFormDesc(pos.description ?? '');
    setFormNumeroTrabajadores(pos.numero_trabajadores != null ? String(pos.numero_trabajadores) : '');
    setFormActividadesRealizadas(pos.actividades_realizadas ?? '');
    setFormHorasActividad(pos.horas_actividad_diarias != null ? String(pos.horas_actividad_diarias) : '');
    setFormRecursosUtilizados(pos.recursos_utilizados ?? '');
    setFormMaterialesAgentes(pos.materiales_agentes ?? '');
    setFormOperaVehiculos(!!pos.opera_vehiculos);
    setFormLicencias(pos.licencias_aceptadas ?? []);
    setFormError('');
    setSelectedHazards(new Set());
    setModalOpen(true);

    const [{ data: catalogData }, { data: hazardData }] = await Promise.all([
      supabase
        .from('iper_hazard_catalog')
        .select('*')
        .order('category')
        .order('display_order'),
      supabase
        .from('job_position_typical_hazards')
        .select('hazard_catalog_id, custom_text')
        .eq('job_position_id', pos.id),
    ]);
    setCatalogItems((catalogData ?? []) as IperHazardCatalogItem[]);
    setSelectedHazards(new Set((hazardData ?? []).map((h: { hazard_catalog_id: string }) => h.hazard_catalog_id)));
    const customTextMap = new Map<string, string>();
    (hazardData ?? []).forEach((h: { hazard_catalog_id: string; custom_text: string | null }) => {
      if (h.custom_text) customTextMap.set(h.hazard_catalog_id, h.custom_text);
    });
    setHazardCustomText(customTextMap);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setFormName('');
    setFormDesc('');
    setFormNumeroTrabajadores('');
    setFormActividadesRealizadas('');
    setFormHorasActividad('');
    setFormRecursosUtilizados('');
    setFormMaterialesAgentes('');
    setFormOperaVehiculos(false);
    setFormLicencias([]);
    setFormError('');
    setSelectedHazards(new Set());
    setHazardCustomText(new Map());
    setExpandedCategories(new Set());
  };

  const toggleCategory = (catValue: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(catValue)) next.delete(catValue); else next.add(catValue);
      return next;
    });
  };

  const toggleHazard = (hazardId: string) => {
    setSelectedHazards(prev => {
      const next = new Set(prev);
      if (next.has(hazardId)) next.delete(hazardId); else next.add(hazardId);
      return next;
    });
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) { setFormError('El nombre del puesto es obligatorio.'); return; }
    if (formOperaVehiculos && formLicencias.length === 0) { setFormError('Elige al menos un tipo de licencia aceptado para el puesto que opera vehículos o maquinaria.'); return; }
    setSaving(true);
    setFormError('');

    const payload = {
      name:        formName.trim(),
      description: formDesc.trim() || null,
      numero_trabajadores:      formNumeroTrabajadores.trim() === '' ? null : Number(formNumeroTrabajadores),
      actividades_realizadas:   formActividadesRealizadas.trim() || null,
      horas_actividad_diarias:  formHorasActividad.trim() === '' ? null : Number(formHorasActividad),
      recursos_utilizados:      formRecursosUtilizados.trim() || null,
      materiales_agentes:       formMaterialesAgentes.trim() || null,
      opera_vehiculos:          formOperaVehiculos,
      licencias_aceptadas:      formOperaVehiculos ? formLicencias : [],
    };

    let savedPositionId: string | null = null;

    if (editing) {
      const { error } = await supabase
        .from('job_positions')
        .update(payload)
        .eq('id', editing.id);

      if (error) {
        setFormError(
          error.code === '23505'
            ? 'Ya existe un puesto con ese nombre en esta empresa.'
            : 'Error al guardar: ' + error.message
        );
        setSaving(false);
        return;
      }
      savedPositionId = editing.id;
      logAudit({
        company_id: effectiveCompanyId,
        action: 'updated',
        entity_type: 'job_position',
        entity_id: editing.id,
        entity_name: payload.name,
      });
      showToast('Puesto actualizado.');
    } else {
      const { data, error } = await supabase
        .from('job_positions')
        .insert({ ...payload, company_id: effectiveCompanyId })
        .select('id')
        .single();

      if (error) {
        setFormError(
          error.code === '23505'
            ? 'Ya existe un puesto con ese nombre en esta empresa.'
            : 'Error al guardar: ' + error.message
        );
        setSaving(false);
        return;
      }
      savedPositionId = data?.id ?? null;
      logAudit({
        company_id: effectiveCompanyId,
        action: 'created',
        entity_type: 'job_position',
        entity_id: data?.id ?? '',
        entity_name: payload.name,
      });
      showToast('Puesto creado.');
    }

    if (savedPositionId) {
      const { error: delHazardsError } = await supabase
        .from('job_position_typical_hazards')
        .delete()
        .eq('job_position_id', savedPositionId);

      if (delHazardsError) {
        setFormError('Error al guardar riesgos típicos: ' + delHazardsError.message);
        setSaving(false);
        return;
      }

      if (selectedHazards.size > 0) {
        const hazardRows = Array.from(selectedHazards).map(hazardCatalogId => ({
          company_id:        effectiveCompanyId,
          job_position_id:   savedPositionId,
          hazard_catalog_id: hazardCatalogId,
          custom_text:       hazardCustomText.get(hazardCatalogId)?.trim() || null,
        }));
        const { error: insHazardsError } = await supabase
          .from('job_position_typical_hazards')
          .insert(hazardRows);

        if (insHazardsError) {
          setFormError('Error al guardar riesgos típicos: ' + insHazardsError.message);
          setSaving(false);
          return;
        }
      }
    }

    setSaving(false);
    closeModal();
    fetchAll();
  };

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async (pos: JobPosition) => {
    setDeletingId(pos.id);
    const { error } = await supabase
      .from('job_positions')
      .delete()
      .eq('id', pos.id);

    if (error) {
      showToast('Error al eliminar: ' + error.message);
    } else {
      logAudit({
        company_id: effectiveCompanyId,
        action: 'deleted',
        entity_type: 'job_position',
        entity_id: pos.id,
        entity_name: pos.name,
      });
      showToast('Puesto eliminado.');
      setPositions(prev => prev.filter(p => p.id !== pos.id));
    }
    setDeletingId(null);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="jp-root">
      <div className="jp-header">
        <div>
          <h3 className="jp-title">Puestos de Trabajo</h3>
          <p className="jp-subtitle">
            {positions.length} puesto{positions.length !== 1 ? 's' : ''} registrado{positions.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="jp-btn-add" onClick={() => setVerInformes(v => !v)} aria-pressed={verInformes}
            style={verInformes ? undefined : { background: 'transparent', border: '1px solid #475569', color: '#cbd5e1' }}>
            {verInformes ? 'Ver puestos' : 'Informes por puesto'}
          </button>
          <button className="jp-btn-add" onClick={openCreate}>
            <PlusIcon /> Agregar Puesto
          </button>
        </div>
      </div>

      {verInformes && (
        <InformesPuestoPanel companyId={effectiveCompanyId} empresa={(impersonated ?? company)?.name ?? ''}
          puedeEditar={profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated} />
      )}

      <div className="jp-table-wrap">
        {loading ? (
          <div className="jp-loading">
            <span className="jp-spinner" />
            <p>Cargando puestos…</p>
          </div>
        ) : fetchError ? (
          <div className="jp-error-box">
            <p>{fetchError}</p>
            <button className="jp-btn-retry" onClick={fetchAll}>Reintentar</button>
          </div>
        ) : positions.length === 0 ? (
          <div className="jp-empty">
            <BriefcaseIcon size={52} />
            <p className="jp-empty-title">Sin puestos de trabajo</p>
            <p className="jp-empty-sub">Haz clic en "Agregar Puesto" para comenzar.</p>
          </div>
        ) : (
          <table className="jp-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Descripción</th>
                <th style={{ width: 160, textAlign: 'center' }}>Trabajadores asignados</th>
                <th style={{ width: 110 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {positions.map(pos => {
                const count = workerCounts[pos.id] ?? 0;
                const canDelete = count === 0;
                const deleteTitle = canDelete
                  ? 'Eliminar puesto'
                  : `No se puede eliminar: tiene ${count} trabajador${count !== 1 ? 'es' : ''} asignado${count !== 1 ? 's' : ''}`;

                return (
                  <tr key={pos.id} className="jp-row">
                    <td className="jp-td-name">{pos.name}</td>
                    <td className="jp-td-desc">
                      {pos.description ?? <span className="jp-empty-cell">—</span>}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {count > 0 ? (
                        <span className="jp-worker-pill">{count}</span>
                      ) : (
                        <span className="jp-empty-cell">—</span>
                      )}
                    </td>
                    <td>
                      <div className="jp-actions">
                        <button
                          className="jp-btn-icon jp-btn-edit"
                          onClick={() => openEdit(pos)}
                          title="Editar puesto"
                        >
                          <EditIcon />
                        </button>
                        <button
                          className={`jp-btn-icon jp-btn-delete ${!canDelete ? 'disabled' : ''}`}
                          onClick={() => canDelete && setPorBorrar(pos)}
                          disabled={!canDelete || deletingId === pos.id}
                          title={deleteTitle}
                        >
                          {deletingId === pos.id
                            ? <span className="jp-spinner jp-spinner-sm" />
                            : <TrashIcon />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {toast && <div className="jp-toast">{toast}</div>}

      {/* ── Modal propio para eliminar (sin confirm() del navegador) ── */}
      {porBorrar && (
        <ModalBorrarPuesto
          mensaje={mensajeBorrarPuesto(porBorrar.name, workerCounts[porBorrar.id] ?? 0, otherWorkerCounts[porBorrar.id] ?? 0)}
          bloqueado={(workerCounts[porBorrar.id] ?? 0) > 0}
          onCancelar={() => setPorBorrar(null)}
          onConfirmar={() => { const p = porBorrar; setPorBorrar(null); handleDelete(p); }}
        />
      )}

      {/* ── Modal ── */}
      {modalOpen && (
        <div
          className="jp-overlay"
          onClick={e => e.target === e.currentTarget && closeModal()}
        >
          <div className="jp-modal">
            <div className="jp-modal-header">
              <h4>{editing ? 'Editar Puesto' : 'Agregar Puesto'}</h4>
              <button className="jp-modal-close" onClick={closeModal}>
                <CloseIcon />
              </button>
            </div>

            <form className="jp-form" onSubmit={handleSave}>
              <div className="jp-field">
                <label>Nombre <span className="jp-required">*</span></label>
                <input
                  type="text"
                  placeholder="ej. Operador de Bodega"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="jp-field">
                <label>Descripción</label>
                <input
                  type="text"
                  placeholder="ej. Manejo de productos químicos en área de almacenamiento"
                  value={formDesc}
                  onChange={e => setFormDesc(e.target.value)}
                />
              </div>

              <div className="jp-field">
                <label>Número de trabajadores asignados</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="ej. 4"
                  value={formNumeroTrabajadores}
                  onChange={e => setFormNumeroTrabajadores(e.target.value)}
                  style={numberInputStyle}
                />
              </div>

              <div className="jp-field">
                <label>Actividades realizadas</label>
                <textarea
                  rows={2}
                  placeholder="ej. Recepción, clasificación y almacenamiento de productos químicos"
                  value={formActividadesRealizadas}
                  onChange={e => setFormActividadesRealizadas(e.target.value)}
                  style={textareaStyle}
                />
              </div>

              <div className="jp-field">
                <label>Horas de jornada laboral</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="ej. 8"
                  value={formHorasActividad}
                  onChange={e => setFormHorasActividad(e.target.value)}
                  style={numberInputStyle}
                />
              </div>

              <div className="jp-field">
                <label>Recursos utilizados (máquinas, equipos, herramientas)</label>
                <textarea
                  rows={2}
                  placeholder="ej. Montacargas, escáner de código de barras, EPP"
                  value={formRecursosUtilizados}
                  onChange={e => setFormRecursosUtilizados(e.target.value)}
                  style={textareaStyle}
                />
              </div>

              <div className="jp-field">
                <label>Materiales y agentes químicos/biológicos</label>
                <textarea
                  rows={2}
                  placeholder="ej. Solventes inflamables, ácidos, material particulado"
                  value={formMaterialesAgentes}
                  onChange={e => setFormMaterialesAgentes(e.target.value)}
                  style={textareaStyle}
                />
              </div>

              <LicenciasPuestoCampo opera={formOperaVehiculos} aceptadas={formLicencias}
                onCambio={(o, l) => { setFormOperaVehiculos(o); setFormLicencias(l); }} />

              <div className="jp-field">
                <label>Riesgos típicos de este puesto</label>
                <div style={hazardsContainerStyle}>
                  {CATEGORIAS_PELIGRO.map(cat => {
                    const items = catalogItems.filter(c => c.category === cat.value);
                    const selectedCount = items.filter(i => selectedHazards.has(i.id)).length;
                    const isExpanded = expandedCategories.has(cat.value);
                    return (
                      <div key={cat.value} style={hazardGroupStyle}>
                        <button
                          type="button"
                          style={hazardGroupHeaderStyle}
                          onClick={() => toggleCategory(cat.value)}
                        >
                          <span>{isExpanded ? '▾' : '▸'} {cat.label}</span>
                          {selectedCount > 0 && (
                            <span style={hazardCountStyle}>
                              {selectedCount} seleccionado{selectedCount !== 1 ? 's' : ''}
                            </span>
                          )}
                        </button>
                        {isExpanded && (
                          <div style={hazardListStyle}>
                            {items.length === 0 ? (
                              <p style={hazardEmptyStyle}>Sin tipos de peligro en esta categoría.</p>
                            ) : (
                              items.map(item => (
                                <React.Fragment key={item.id}>
                                  <label style={hazardCheckboxRowStyle}>
                                    <input
                                      type="checkbox"
                                      checked={selectedHazards.has(item.id)}
                                      onChange={() => toggleHazard(item.id)}
                                      style={{ marginTop: 3, flexShrink: 0, width: 'auto', height: 'auto' }}
                                    />
                                    <span>{item.hazard_type}</span>
                                  </label>
                                  {item.hazard_type === 'Otros' && selectedHazards.has(item.id) && (
                                    <input
                                      type="text"
                                      placeholder="Especifica el peligro"
                                      value={hazardCustomText.get(item.id) ?? ''}
                                      onChange={e => {
                                        const next = new Map(hazardCustomText);
                                        next.set(item.id, e.target.value);
                                        setHazardCustomText(next);
                                      }}
                                      style={{
                                        marginTop: 4, marginLeft: 24, width: 'calc(100% - 24px)',
                                        background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
                                        color: '#e2e8f0', padding: '6px 10px', fontSize: '0.8rem',
                                      }}
                                    />
                                  )}
                                </React.Fragment>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {formError && <p className="jp-error">{formError}</p>}

              <div className="jp-modal-footer">
                <button type="button" className="jp-btn-cancel" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="jp-btn-save" disabled={saving}>
                  {saving && <span className="jp-spinner jp-spinner-sm" />}
                  {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Agregar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ModalBorrarPuesto({ mensaje, bloqueado, onCancelar, onConfirmar }: {
  mensaje: string; bloqueado: boolean; onCancelar: () => void; onConfirmar: () => void;
}) {
  const cancelar = React.useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancelar.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancelar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onCancelar]);
  return (
    <div className="jp-overlay" onClick={e => e.target === e.currentTarget && onCancelar()}>
      <div className="jp-modal jp-modal-confirmar" role="dialog" aria-modal="true" aria-labelledby="jp-borrar-titulo">
        <div className="jp-modal-header">
          <h4 id="jp-borrar-titulo">Eliminar puesto</h4>
        </div>
        <p className="jp-confirmar-texto">{mensaje}</p>
        <div className="jp-modal-footer">
          <button ref={cancelar} type="button" className="jp-btn-cancel" onClick={onCancelar}>Cancelar</button>
          <button type="button" className="jp-btn-save jp-btn-peligro" disabled={bloqueado} onClick={onConfirmar}>Eliminar puesto</button>
        </div>
      </div>
    </div>
  );
}

// ── Estilos: Riesgos típicos ─────────────────────────────────────────────────────

const textareaStyle: React.CSSProperties = {
  padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155',
  borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', width: '100%',
  boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical',
};

const numberInputStyle: React.CSSProperties = {
  padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155',
  borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', width: '100%',
  boxSizing: 'border-box',
};

const hazardsContainerStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 6,
  border: '1px solid #334155', borderRadius: 8, padding: 6,
  maxHeight: 'min(50vh, 420px)', overflowY: 'auto',
};

const hazardGroupStyle: React.CSSProperties = {
  border: '1px solid #334155', borderRadius: 6, overflow: 'hidden', flexShrink: 0,
};

const hazardGroupHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.03)',
  border: 'none', color: '#cbd5e1', fontSize: '0.84rem', fontWeight: 600,
  cursor: 'pointer', textAlign: 'left',
};

const hazardCountStyle: React.CSSProperties = {
  fontSize: '0.72rem', color: '#2FBF71', fontWeight: 700,
};

const hazardListStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
  padding: '8px 10px', borderTop: '1px solid #334155',
};

const hazardCheckboxRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 8,
  fontSize: '0.82rem', color: '#cbd5e1', cursor: 'pointer',
  width: '100%', boxSizing: 'border-box',
};

const hazardEmptyStyle: React.CSSProperties = {
  fontSize: '0.78rem', color: '#64748b', margin: 0,
};

// ── Icons ──────────────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function BriefcaseIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
      <line x1="12" y1="12" x2="12" y2="12.01" />
      <path d="M2 12h20" />
    </svg>
  );
}
